import { FaxLogRecord } from '@mummssoftware/utils';
import {
  formatPhoneNumber,
  name,
  nameWithCredentials,
  timestampToDisplay,
} from '@mummssoftware/utils/formatters';
import { ColDef, GridOptions } from 'ag-grid-community';
import { flowRight, property } from 'lodash';
import { FaxExpandToggle } from './FaxExpandToggle';
import defaults from '../../translations/en.json';

/** Rows with a queue id but no callback after this long show Unknown instead of Pending. */
const PENDING_STALE_MS = 7 * 24 * 60 * 60 * 1000;

const hasSendFaxQueueId = (data: FaxLogRecord): boolean =>
  Boolean(data.sendFaxQueueId?.trim());

const isPendingStale = (data: FaxLogRecord): boolean => {
  const faxDate = data.faxDateTime_shifted ?? data.faxDateTime;
  if (!faxDate) {
    return false;
  }
  const sentAt = new Date(faxDate).getTime();
  if (Number.isNaN(sentAt)) {
    return false;
  }
  return Date.now() - sentAt > PENDING_STALE_MS;
};

const expandToggleCellStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
};

const ExpandToggle: ColDef = {
  headerName: '',
  field: 'expandToggle',
  width: 44,
  minWidth: 44,
  maxWidth: 44,
  suppressMenu: true,
  sortable: false,
  resizable: false,
  cellRendererFramework: FaxExpandToggle,
  cellStyle: expandToggleCellStyle,
};

const PatientName: ColDef = {
  headerName: 'Patient',
  minWidth: 160,
  valueGetter: ({ data }) => name(data.patientFirstName, data.patientLastName),
  flex: 1.8,
};

const centeredNumericColDef: Pick<
  ColDef,
  'cellClass' | 'headerClass' | 'filter' | 'menuTabs' | 'suppressMenu'
> = {
  cellClass: 'med-center',
  headerClass: 'center-align',
  filter: false,
  menuTabs: [],
  suppressMenu: true,
};

const PatientNumber: ColDef = {
  field: 'patientNumber',
  headerName: '#',
  width: 55,
  flex: 1,
  ...centeredNumericColDef,
};

const faxLogDateSortValue = (data?: FaxLogRecord) =>
  data?.faxDateTime_shifted ?? data?.faxDateTime ?? '';

const FaxDateTime: ColDef = {
  field: 'faxDateTime_shifted',
  headerName: 'Date',
  minWidth: 80,
  valueGetter: flowRight(
    timestampToDisplay,
    property('data.faxDateTime_shifted'),
  ),
  flex: 1.5,
  sort: 'desc',
  comparator: (_valueA, _valueB, nodeA, nodeB) =>
    faxLogDateSortValue(nodeA?.data).localeCompare(
      faxLogDateSortValue(nodeB?.data),
    ),
};

const RecipientName: ColDef = {
  field: 'recipientName',
  headerName: 'Recipient',
  minWidth: 160,
  flex: 1.8,
};

const FaxNumber: ColDef = {
  headerName: 'Fax Number',
  minWidth: 130,
  valueGetter: flowRight(
    formatPhoneNumber,
    property('data.recipientFaxNumber'),
  ),
  flex: 1.5,
};

const SenderName: ColDef = {
  headerName: 'Sender',
  minWidth: 160,
  valueGetter: ({ data }: { data: FaxLogRecord }) =>
    nameWithCredentials(
      data.personFirstName,
      data.personLastName,
      data.personCredentials,
    ),
  flex: 1.8,
};

const NumberOfPages: ColDef = {
  field: 'numberOfPages',
  headerName: 'Pages',
  width: 80,
  flex: 1,
  ...centeredNumericColDef,
};

/** Max length for SFax failure detail shown in the Delivery column. */
const DELIVERY_DETAIL_MAX_LEN = 25;

const truncateWithEllipsis = (text: string, maxLen: number): string =>
  text.length <= maxLen ? text : `${text.slice(0, maxLen)}...`;

const deliveryStatusText = (data: FaxLogRecord): string => {
  if (data.faxDelivered === true) return defaults.delivered;
  if (data.faxDelivered === false) {
    return data.faxDeliveryStatusText
      ? `${defaults.deliveryFailed}: ${truncateWithEllipsis(
          data.faxDeliveryStatusText,
          DELIVERY_DETAIL_MAX_LEN,
        )}`
      : defaults.deliveryFailed;
  }
  if (!hasSendFaxQueueId(data)) return '';
  if (isPendingStale(data)) return defaults.deliveryUnknown;
  return defaults.deliveryPending;
};

const FaxDeliveryStatus: ColDef = {
  headerName: 'Delivery',
  minWidth: 95,
  flex: 1.2,
  valueGetter: ({ data }: { data: FaxLogRecord }) => deliveryStatusText(data),
  cellStyle: ({ data }: { data: FaxLogRecord }) => {
    if (data?.faxDelivered === true) return { color: '#2e7d32' };
    if (data?.faxDelivered === false) return { color: '#c62828' };
    if (!hasSendFaxQueueId(data)) return { color: '#9e9e9e' };
    if (isPendingStale(data)) return { color: '#b8860b' };
    return { color: '#757575' };
  },
  ...centeredNumericColDef,
};

const smallWidgetFields: ColDef[] = [PatientName, FaxNumber, FaxDateTime];

const fullWidgetFields: ColDef[] = [
  ExpandToggle,
  PatientName,
  PatientNumber,
  FaxDateTime,
  SenderName,
  RecipientName,
  FaxNumber,
  NumberOfPages,
  FaxDeliveryStatus,
];

export const getFaxLogColumnDefs = (smallWidget: boolean): ColDef[] =>
  smallWidget ? smallWidgetFields : fullWidgetFields;

export const faxLogGridOptions = (smallWidget: boolean): GridOptions => ({
  defaultColDef: {
    resizable: true,
    sortable: true,
    filter: 'agTextColumnFilter',
    menuTabs: ['filterMenuTab'],
  },
  columnDefs: getFaxLogColumnDefs(smallWidget),
});
